'use client'
import { useLazyGetCancellationPolicyQuery } from '@/src/api/rtkQuery/hotels/hotelReviewApi'
import { ReviewRateOption } from '@/src/types/hotels/details'
import { formatHotelCurrency } from '@/src/utils/hotels'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'
import RoomVariantRow from './roomVariantRow'

export interface ReviewContext {
    productId: string
    uid: string
    echoToken: string
}

export interface RoomVariantRowContainerProps {
    option: ReviewRateOption
    variant: 'mobile' | 'desktop'
    reviewContext: ReviewContext
    onSelect: (rId: string, arNo: string) => void
    isLast: boolean
}

const RoomVariantRowContainer = ({ option, variant, reviewContext, onSelect, isLast }: RoomVariantRowContainerProps) => {
    const t = useTranslations('hotels')
    const locale = useLocale()
    const [cancellationOpen, setCancellationOpen] = useState(false)
    const [triggerPolicy, { data: policyData, isFetching }] = useLazyGetCancellationPolicyQuery()

    const mealLabel =
        option.mealKind === 'roomOnly'
            ? t('roomOnlyMeal')
            : option.mealKind === 'breakfast'
              ? t('roomWithBreakfast')
              : option.mealName

    const format = useCallback(
        (amount: number) => formatHotelCurrency(amount, option.price.currency, locale),
        [option.price.currency, locale]
    )

    const cancellationText = useMemo(() => {
        // CT returns the policy as a top-level string; the legacy shape nested it under
        // `data`. Root first, with the old reads kept for anything still resolving through
        // the pre-migration path.
        const value =
            policyData?.cancellationPolicy?.trim() || policyData?.data?.ns?.crg?.val || policyData?.data?.nrf
        return value || t('cancellationPolicyUnavailable')
    }, [policyData, t])

    const onToggleCancellation = useCallback(() => {
        setCancellationOpen(prev => {
            const next = !prev
            if (next && !policyData) {
                triggerPolicy({
                    roomId: option.rId,
                    pid: reviewContext.productId,
                    uid: reviewContext.uid,
                    echoToken: reviewContext.echoToken,
                    index: option.arNo
                })
            }
            return next
        })
    }, [policyData, triggerPolicy, option.rId, option.arNo, reviewContext])

    return (
        <RoomVariantRow
            variant={variant}
            mealLabel={mealLabel}
            inclusions={option.inclusions}
            cancellationLabel={t('cancellationPolicy')}
            cancellationOpen={cancellationOpen}
            cancellationLoading={isFetching}
            cancellationText={cancellationText}
            onToggleCancellation={onToggleCancellation}
            dealBadge={option.price.hasDiscount ? `${option.price.discountPct}% ${t('off')}` : undefined}
            wasPriceText={option.price.hasDiscount ? format(option.price.was) : undefined}
            nowPriceText={format(option.price.now)}
            perStayText={t('perStayNightsRooms', { nights: option.nights, rooms: option.rooms })}
            selectLabel={t('selectRoomCta')}
            onSelect={() => onSelect(option.rId, option.arNo)}
            isLast={isLast}
        />
    )
}

export { RoomVariantRowContainer }
export default RoomVariantRowContainer
